import React, { useEffect, useRef, useState } from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { CloseIcon, InfoIcon, UploadIcon } from "../../../../assets/ui/icons";
import {
  ZOOM_MAX,
  ZOOM_MIN,
  clampOffset,
  clampZoom,
  offsetBounds,
  sizeLabel,
  sizeWarning,
} from "./imageAssets";
import { croppedFile } from "./cropImage";
import { ACCEPT_IMAGES } from "../../../common/u1/uploadTypes";

/* ONE dialog, both assets. The design draws this twice — "Update Banner" and "Update Logo" — with the same layout and different constraints, so the difference... */

const Chip = ({ children, icon }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-lg border border-[var(--claimed-border)]",
      "bg-[var(--claimed-bg)] px-2.5 py-1.5",
      "text-[11.5px] font-semibold text-[var(--ink-2)] font-sans"
    )}
  >
    {icon}
    {children}
  </span>
);

export const ImageCropModal = ({ spec, currentUrl, onClose, onApply }) => {
  const [file, setFile] = useState(null);
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [applying, setApplying] = useState(false);

  const frameRef = useRef(null);
  const imageRef = useRef(null);
  const dragStart = useRef(null);
  const inputRef = useRef(null);
  /* The object URL is revoked on unmount and whenever it is replaced, so
     choosing several images in a row does not leak them. */
  const objectUrl = useRef(null);

  const setChosen = (next) => {
    if (!next) return;
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    const url = URL.createObjectURL(next);
    objectUrl.current = url;
    setFile(next);
    setSrc(url);
    /* A new image starts unpositioned, so it uploads untouched unless the
       viewer moves it. */
    setZoom(ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
  };

  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    []
  );

  const bounds = () => {
    const frame = frameRef.current;
    const image = imageRef.current;
    return offsetBounds({
      zoom,
      frameWidth: frame?.clientWidth,
      frameHeight: frame?.clientHeight,
      naturalWidth: image?.naturalWidth,
      naturalHeight: image?.naturalHeight,
    });
  };

  const onZoom = (next) => {
    const z = clampZoom(next);
    setZoom(z);
    /* Zooming out can leave the image outside its own bounds, so the offset is
       re-clamped against the new zoom rather than left stale. */
    const frame = frameRef.current;
    const image = imageRef.current;
    setOffset((prev) =>
      clampOffset(
        prev,
        offsetBounds({
          zoom: z,
          frameWidth: frame?.clientWidth,
          frameHeight: frame?.clientHeight,
          naturalWidth: image?.naturalWidth,
          naturalHeight: image?.naturalHeight,
        })
      )
    );
  };

  const startDrag = (e) => {
    if (!src) return;
    setDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      offset,
    };
  };

  const onDrag = (e) => {
    if (!dragging || !dragStart.current) return;
    const next = {
      x: dragStart.current.offset.x + (e.clientX - dragStart.current.x),
      y: dragStart.current.offset.y + (e.clientY - dragStart.current.y),
    };
    setOffset(clampOffset(next, bounds()));
  };

  const endDrag = () => {
    setDragging(false);
    dragStart.current = null;
  };

  const onDrop = (e) => {
    e.preventDefault();
    setChosen(e.dataTransfer?.files?.[0]);
  };

  const apply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const frame = frameRef.current;
      const out = await croppedFile({
        file,
        image: imageRef.current,
        spec,
        zoom,
        offset,
        frameWidth: frame?.clientWidth,
        frameHeight: frame?.clientHeight,
      });
      onApply?.(out);
    } finally {
      setApplying(false);
    }
  };

  const warning = sizeWarning(file, spec);
  const preview = src || currentUrl;

  return (
    <div
      className={cn(
        "u1-root flex max-h-[85vh] w-[min(92vw,480px)] flex-col overflow-hidden",
        "rounded-[var(--radius-modal)] shadow-[shadow:var(--shadow-lg)] bg-white text-left"
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-4 px-5 pt-5 sm:px-6">
        <h2 className="m-0 text-[17px] font-bold text-[var(--ink)] font-sans">
          {spec.title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            "bg-[var(--slate-2)] text-[var(--muted)] transition-colors",
            "hover:bg-[var(--line)] focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
          )}
        >
          <CloseIcon size={14} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
        <div className="flex flex-wrap gap-2">
          <Chip icon={<InfoIcon size={13} className="text-[var(--navy)]" />}>
            {spec.ratioLabel}
          </Chip>
          <Chip>{sizeLabel(spec)}</Chip>
          <Chip>{spec.maxLabel}</Chip>
        </div>

        {/* The frame. Its aspect ratio is the asset's, so the banner is a wide
            strip and the logo a square — which is what makes one component
            serve both. */}
        <div
          ref={frameRef}
          role="button"
          tabIndex={0}
          aria-label={src ? "Reposition image" : "Choose an image"}
          onClick={() => (src ? undefined : inputRef.current?.click())}
          onKeyDown={(e) => {
            if (src) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onMouseDown={startDrag}
          onMouseMove={onDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          style={{ aspectRatio: String(spec.ratio) }}
          className={cn(
            "relative mt-4 w-full overflow-hidden rounded-[10px]",
            "bg-[var(--field-inset)]",
            src ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
            /* The logo's square would otherwise stretch the full dialog width;
               the design centres it at a readable size. */
            spec.ratio === 1 && "mx-auto max-w-[180px]"
          )}
        >
          {preview && (
            <img
              ref={imageRef}
              src={preview}
              alt=""
              draggable={false}
              /* Transform is computed per drag frame, so it cannot be a
                 Tailwind class. */
              style={{
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
              }}
              className="pointer-events-none absolute left-1/2 top-1/2 h-full w-full object-cover"
            />
          )}

          {/* The safe-crop grid the design shows — rule-of-thirds lines over
              the frame. Decorative; the copy below explains it. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <span className="absolute inset-y-0 left-1/3 w-px bg-[var(--line-2)]/70" />
            <span className="absolute inset-y-0 left-2/3 w-px bg-[var(--line-2)]/70" />
            <span className="absolute inset-x-0 top-1/3 h-px bg-[var(--line-2)]/70" />
            <span className="absolute inset-x-0 top-2/3 h-px bg-[var(--line-2)]/70" />
          </div>

          {!src && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-4 text-center">
              <span className="text-[var(--muted-2)]">
                <UploadIcon size={26} />
              </span>
              <span className="text-[12.5px] text-[var(--muted)] font-sans">
                Drag &amp; drop or{" "}
                <b className="font-bold text-[var(--coral)]">choose an image</b>
              </span>
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_IMAGES}
          aria-label={`Choose ${spec.label}`}
          onChange={(e) => setChosen(e.target.files?.[0])}
          className="sr-only"
        />

        <div className="mt-4 flex items-center gap-3">
          <label
            htmlFor="u1-crop-zoom"
            className="text-[12.5px] font-semibold text-[var(--ink-2)] font-sans"
          >
            Zoom
          </label>
          <input
            id="u1-crop-zoom"
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step="0.01"
            value={zoom}
            disabled={!src}
            onChange={(e) => onZoom(e.target.value)}
            className="h-1.5 flex-1 cursor-pointer accent-[var(--coral)] disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <p className="mb-0 mt-2.5 text-[12px] leading-[1.5] text-[var(--muted)] font-sans">
          Drag to reposition and use the slider to zoom once an image is added.
          The grid shows the safe crop area.
        </p>

        {/* Over the recommended size: said out loud, but not blocked. The live
            upload does not reject on size and neither does the API. */}
        {warning && (
          <p
            role="alert"
            className="mb-0 mt-2.5 text-[12px] leading-[1.5] text-[var(--gold-deep)] font-sans"
          >
            {warning}
          </p>
        )}
      </div>

      <div className="shrink-0 px-5 pb-5 sm:px-6">
        <Button
          variant="coralSolid"
          onClick={apply}
          /* Nothing chosen, nothing to set. */
          disabled={!file || applying}
          className="w-full"
        >
          {applying ? "Working…" : spec.cta}
        </Button>
      </div>
    </div>
  );
};

export default ImageCropModal;
